import React from 'react';
import clsx from 'clsx';

export type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'h7'
  | 'body1'
  | 'body2'
  | 'body3'
  | 'body4'
  | 'label1'
  | 'label2'
  | 'label2SemiBold'
  | 'label3'
  | 'label3SemiBold'
  | 'label4'
  | 'label5'
  | 'button1'
  | 'button1light'
  | 'button2';

const variantMap: Record<TypographyVariant, { tag: React.ElementType; className: string }> = {
  h1: {
    tag: 'h1',
    className:
      'text-auth-ink text-typo-h1 md:text-typo-h1-md font-semibold tracking-[-0.0156rem] md:tracking-[-0.04rem]',
  },
  h2: {
    tag: 'h2',
    className:
      'text-auth-ink text-typo-h2 md:text-typo-h2-md font-semibold tracking-[-0.0156rem] md:tracking-[-0.04rem]',
  },
  h3: {
    tag: 'h3',
    className:
      'text-auth-ink text-typo-h3 md:text-typo-h3-md font-medium md:font-semibold tracking-[-0.0156rem] md:tracking-[-0.04rem]',
  },
  h4: {
    tag: 'h4',
    className: 'text-auth-ink text-typo-h4 md:text-typo-h4-md font-medium md:font-semibold',
  },
  h5: {
    tag: 'h5',
    className: 'text-auth-ink text-typo-h5 md:text-typo-h5-md font-medium md:font-semibold',
  },
  h6: { tag: 'h6', className: 'text-auth-ink text-typo-h6 md:text-typo-h6-md font-medium' },
  h7: { tag: 'h6', className: 'text-auth-ink text-typo-h7 font-medium md:font-semibold' },
  body1: { tag: 'p', className: 'text-auth-ink text-typo-body1 font-regular' },
  body2: { tag: 'p', className: 'text-auth-ink text-typo-body2 font-regular' },
  body3: { tag: 'p', className: 'text-auth-ink text-typo-body3 font-regular' },
  body4: { tag: 'p', className: 'text-auth-ink text-typo-body4 font-regular' },
  label1: { tag: 'span', className: 'text-auth-ink text-typo-label1 font-medium' },
  label2: {
    tag: 'span',
    className: 'text-auth-ink text-typo-label2 font-medium uppercase tracking-[0.04em]',
  },
  label2SemiBold: { tag: 'span', className: 'text-auth-ink text-typo-label2 font-semibold' },
  label3: {
    tag: 'span',
    className: 'text-auth-ink text-typo-label3 font-medium uppercase tracking-[0.04em]',
  },
  label3SemiBold: { tag: 'span', className: 'text-auth-ink text-typo-label3 font-semibold' },
  label4: {
    tag: 'span',
    className: 'text-auth-ink text-typo-label4 font-medium uppercase tracking-[0.04em]',
  },
  label5: {
    tag: 'span',
    className: 'text-auth-ink text-typo-label5 font-medium uppercase tracking-[0.04em]',
  },
  button1: {
    tag: 'span',
    className: 'text-auth-ink text-typo-button1 font-semibold uppercase tracking-[0.04em]',
  },
  button1light: {
    tag: 'span',
    className: 'text-auth-ink text-typo-button1 font-normal uppercase tracking-[0.04em]',
  },
  button2: {
    tag: 'span',
    className: 'text-auth-ink text-typo-button2 font-semibold uppercase tracking-[0.04em]',
  },
};

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: TypographyVariant;
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
}

export const Typography: React.FC<TypographyProps> = ({
  variant,
  as,
  children,
  className,
  ...rest
}) => {
  const { tag, className: variantClass } = variantMap[variant];
  const Tag = as || tag;
  return (
    <Tag className={clsx(variantClass, className)} {...rest}>
      {children}
    </Tag>
  );
};

export default Typography;
